// src/App.tsx — FINAL CLEAN VERSION (Permanent Dark Mode)

import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider, CssBaseline, Box } from "@mui/material";

import AppSidebar from "./components/Sidebar/Sidebar";
import Navbar from "./components/Navbar/Navbar";

import Dashboard from "./pages/DashboardPage/Dashboard";
import AllTestsPage from "./pages/AllTestsPage/AllTestsPage";
import DeviceRegistryPage from "./pages/DeviceRegistryPage/DeviceRegistryPage";
import PositiveCasesPage from "./pages/PositiveCasesPage/PositiveCasesPage";
import SettingsPage from "./pages/SettingsPage/SettingsPage";
import MapPage from "./pages/MapPage/MapPage";
import LogoutPage from "./pages/LogoutPage/LogoutPage";
import NotificationsPage from "./pages/NotificationsPage/NotificationsPage";

import SignupPage from "./pages/SignupPage/SignupPage";
import LoginPage from "./pages/LoginPage/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage/ResetPasswordPage";

import UnauthorizedPage from "./pages/UnauthorizedPage";

import NotificationSimulator from "./components/NotificationSimulator";

import theme from "./theme"; // ← Static dark theme
import { AuthProvider, useAuth } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import ProtectedRoute from "./components/ProtectedRoute";

/* Public Route — Only accessible when NOT logged in */
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/" replace /> : <>{children}</>;
};

/* Main Layout */
const MainLayout: React.FC = () => {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleSidebarToggle = () => setSidebarOpen((prev) => !prev);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Simulated notifications */}
      {user && <NotificationSimulator user={user} />}

      {/* Sidebar */}
      {user && (
        <AppSidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
      )}

      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* Navbar */}
        {user && (
          <Navbar
            onMenuToggle={handleSidebarToggle}
          />
        )}

        {/* Page Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 3 },
            backgroundColor: "background.default",
          }}
        >
          <Routes>
            {/* Public Routes */}
            <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
            <Route path="/reset-password/:token" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />

            {/* Protected Routes */}
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/all-tests" element={<ProtectedRoute><AllTestsPage /></ProtectedRoute>} />
            <Route path="/device-registry" element={<ProtectedRoute><DeviceRegistryPage /></ProtectedRoute>} />
            <Route path="/positive-cases" element={<ProtectedRoute><PositiveCasesPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/map" element={<ProtectedRoute><MapPage /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
            <Route path="/logout" element={<ProtectedRoute><LogoutPage /></ProtectedRoute>} />

            {/* Fallbacks */}
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="*" element={<Navigate to={user ? "/" : "/login"} replace />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

/* Root App */
const App: React.FC = () => {
  return (
    <AuthProvider>
      <NotificationProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <MainLayout />
          </Router>
        </ThemeProvider>
      </NotificationProvider>
    </AuthProvider>
  );
};

export default App;